import React, { useState, Fragment, useEffect } from "react";

// ----- Common Component -----
import Toaster from "common/Toaster";

// ----- Apollo Graphql Provider -----
import { useMutation } from "@apollo/client";
import { SIGN_IN } from "./mutation";

import DASHBOARD_IMG from "assets/images/dashboard.png";

// ----- Material UI Provider -----
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { Visibility, VisibilityOff } from "@mui/icons-material";

// ----- UI Component -----
import { ToastContainer } from "react-toastify";
import { getIpAddress } from "utils";

const Login = () => {
  // ----- Material UI Component -----
  const matches = useMediaQuery("(max-width:980px)");

  // ----- States -----
  const [payload, setPayload] = useState({ email: "", password: "" });
  const [error, setError] = useState({ email: "", password: "" });
  const [passwordVisibility, setPasswordVisibility] = useState(false);

  // ----- Sign In mutation -----
  const [signIn, { loading }] = useMutation(SIGN_IN, {
    onCompleted: (res) => {
      if (!res.loginUser.user.isStaff) {
        Toaster({
          message: "You are not an authorized person to login!",
          type: "error",
        });
      } else {
        localStorage.setItem("token", res.loginUser.access);
        localStorage.setItem("refresh", res.loginUser.refresh);
        window.location.href = "/admin/dashboard";
      }
    },
    onError: (err) => {
      if (err?.graphQLErrors[0]?.extensions?.errors) {
        setError(err.graphQLErrors[0].extensions.errors);
      } else {
        Toaster({
          message: err.message,
          type: "error",
        });
      }
    },
  });

  // ----- Input onChange function -----
  const handleInputChange = (e) => {
    setError({ ...error, [e.target.name]: "" });
    setPayload({ ...payload, [e.target.name]: e.target.value });
  };

  // ----- Password hide show function -----
  const passwordVisibilityHandler = () => {
    setPasswordVisibility(!passwordVisibility);
  };

  // ----- Enter key press function -----
  const handleKeypress = (e) => {
    if (e.key === "Enter") {
      handleLogin();
    }
  };

  // ----- Login submit function -----
  const handleLogin = (e) => {
    if (!payload.email) {
      setError({ ...error, email: "Please enter login id!" });
      return;
    }
    if (!payload.password) {
      setError({ ...error, password: "Please enter password!" });
      return;
    }
    signIn({ variables: payload });
  };

  useEffect(() => {
    getIpAddress();
  }, []);

  return (
    <Fragment>
      <Box
        sx={{
          position: "relative",
          height: "100vh",
          width: "100%",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            left: 25,
            top: 25,
            widht: 50,
            height: 50,
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Hi, Welcome Back
          </Typography>
        </Box>
        {!matches && (
          <Box
            sx={{
              textAlign: "center",
              background: "#F9F9FD",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              flex: 1,
            }}
          >
            {/* <Typography variant="h4" sx={{ fontWeight: 600 }}>Hi, Welcome Back</Typography> */}
            <Box sx={{ width: "80%" }}>
              <img
                style={{ marginTop: "20px", width: "100%" }}
                src={DASHBOARD_IMG}
                alt="illustration Not Found"
              />
            </Box>
          </Box>
        )}
        <Box
          sx={{
            width: "480px",
            maxWidth: "480px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "left",
          }}
        >
          <Box sx={{ width: "85%" }}>
            <Typography mb={3} variant="h5" sx={{ fontWeight: 600 }}>
              Sign in to Supergym
            </Typography>
          </Box>
          <TextField
            sx={{ mb: 3, width: "85%" }}
            variant="outlined"
            type="text"
            name="email"
            label="Email"
            value={payload?.email ?? ""}
            error={error.email !== ""}
            helperText={error && error.email}
            onChange={handleInputChange}
            onKeyPress={handleKeypress}
          />
          <TextField
            sx={{ mb: 3, width: "85%" }}
            variant="outlined"
            type={passwordVisibility ? "text" : "password"}
            name="password"
            label="Password"
            value={payload?.password ?? ""}
            error={error.password !== ""}
            helperText={error && error.password}
            onChange={handleInputChange}
            onKeyPress={handleKeypress}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={passwordVisibilityHandler}
                    onMouseDown={passwordVisibilityHandler}
                    edge="end"
                  >
                    {passwordVisibility ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <LoadingButton
            loading={loading}
            sx={{ width: "85%" }}
            size="large"
            type="submit"
            variant="contained"
            onClick={handleLogin}
          >
            Login
          </LoadingButton>
        </Box>
      </Box>
      <ToastContainer />
    </Fragment>
  );
};

export default React.memo(Login);
